import { on } from '@ngrx/store';
import {
  addStudioImage,
  addStudioVideo,
  deleteStudioImage,
  deleteStudioVideo,
  resetStudio,
  resetStudioCrudEvents,
  setChangeTookPlace,
  setCurrentStudioProjectID,
  setCurrentStudioSceneIndex,
  saveScene,
  setSceneDescriptorValueByKey,
  setLoadedProject,
  setStudioData,
  setStudioError,
  setStudioMode,
  setStudioResourceDelete,
  setStudioResourcePatch,
  setStudioResourcePost,
  addStudioSceneBase,
  removeStudioScene,
  setSceneDurations,
  selectCell,
  deselectAllCells,
  textStyleChange,
  selectMedia,
  deselectMedia,
  addVideoInstance,
  updateVideoInstance,
  saveShape,
  removeText,
  selectShape,
  deselectAllShapes,
  setShapeDescriptorAttribute,
  setCurrentStudioSceneId,
  setProjectLabel,
  setPanelStable,
  setStudioState,
  setStudioProjects,
  deleteProjectFailure,
  deleteProjectSuccess,
  deleteProject,
  setStudioCrudStateValue,
} from './studio.actions';
import {
  StudioScene,
  TextCell,
  StudioVideo,
  StudioImage,
  VideoInstance,
} from './studio.types';
import { IRIToId } from '../../../util/iri';
import { FileDescriptor } from '../../../elements/resource/types/shape.type';
import { createImmerReducer } from 'ngrx-immer/store';
import { set } from 'lodash';
import { EditEvent } from '../../../projects/project.interface';
import { CrudState } from '../../../store/store.types';
export type StudioResourceType = 'scene' | 'image' | 'video' | 'project';
export interface StudioCrudEvents {
  posts: Record<string, StudioResourceType>;
  patches: Record<string, StudioResourceType>;
  deletes: Record<string, StudioResourceType>;
}

export interface StudioProject {
  id: string;
  name: string;
  organisationId?: string;
  colorPalette?: Record<string, string>;
  fontFamily?: string;
  mode?: 'landscape' | 'portrait';
  edits?: Record<string, EditEvent>;
}

export interface StudioState {
  projects?: Record<string, StudioProject>;
  deleteProjectState?: Record<
    string,
    {
      loading?: boolean;
      error?: string;
    }
  >;
  crudState: CrudState;
  mode: string;
  data: Record<string, unknown>;
  panelStable: boolean;

  currentSceneId?: string;
  currentProjectID?: string;
  scenes: Record<string, StudioScene>;
  sceneDurations?: number[];
  videos?: Record<string, StudioVideo>;
  images?: Record<string, StudioImage>;
  crudEvents: StudioCrudEvents;
  changeTookPlace: number;
  videoControlData?: VideoControlData;
  selectedCells?: Record<string, boolean>;
  selectedShapes?: Record<string, boolean>;
  selectedMedia?: VideoInstance;
  selectedVideoInstanceId?: string;
  selectedImageId?: string;
  isPortraitMode: boolean;
  state: 'loading' | 'loaded' | 'error';
  error?: string;
}

export interface VideoControlData {
  currentTokenPosition: number;
  totalDuration: number;
  sceneSections: Array<VideoControlSection>;
  startsBySceneId: Record<string, number>;
}

export interface VideoControlSection {
  sceneId: string;
  start?: number;
  end?: number;
  duration: number;
  index: number;
  selectedMedia: StudioVideo | StudioImage;
}
export const initialStudioState: StudioState = {
  error: undefined,
  mode: 'default',
  data: {},
  scenes: {},
  crudEvents: {
    posts: {},
    patches: {},
    deletes: {},
  },
  crudState: {
    new: 'idle',
    index: 'idle',
    list: 'idle',
    delete: 'idle',
  },
  changeTookPlace: 0,
  selectedCells: {},
  selectedShapes: {},
  isPortraitMode: false,
  panelStable: true,
  projects: {},
  state: 'loading',
};

export const studioReducer = createImmerReducer(
  initialStudioState,

  on(setStudioState, (state, { value }) => {
    state.state = value;
    return state;
  }),
  on(setStudioCrudStateValue, (state, { key, value }) => {
    state.crudState[key] = value;
    return state;
  }),
  on(setStudioProjects, (state, { projects }) => {
    state.projects = projects;
    return state;
  }),

  on(setLoadedProject, (state, { project: projectResource, scenes: files }) => {
    // console.log('set-scenes', files);
    // -- //
    const sortFcn = (a, b) =>
      a.literals.descriptor.index - b.literals.descriptor.index;

    const images = Object.values(files)
      .filter(
        scene => (scene.literals.descriptor as FileDescriptor).type == 'image',
      )
      .sort(sortFcn)
      .reduce(
        (object, image, index) => ({
          ...object,
          [IRIToId(image.IRI)]: {
            id: IRIToId(image.IRI),
            ...image.literals.descriptor,
          } as StudioImage,
        }),
        {} as Record<string, StudioImage>,
      );

    // let videoInstances = {}; // -- // -- //
    // console.log('images', images); // -- //
    const videos = Object.values(files)
      .filter(
        scene => (scene.literals.descriptor as FileDescriptor).type == 'video',
      )
      .sort(sortFcn)
      .reduce(
        (object, video, index) => {
          // const { instances } = video.literals.descriptor as StudioVideo;
          // videoInstances = {
          //   ...videoInstances,
          //   ...instances,
          // };
          return {
            ...object,
            [IRIToId(video.IRI)]: {
              id: IRIToId(video.IRI),
              ...video.literals.descriptor,
              positionIndex: index,
            } as StudioVideo,
          };
        },
        {} as Record<string, StudioVideo>,
      );

    const scenes = Object.values(files)
      .filter(
        scene => (scene.literals.descriptor as FileDescriptor).type == 'scene',
      )
      .sort(sortFcn);
    // .slice(0, 1);

    // Cheking indexes

    const indexesMap = scenes.reduce(
      (object, scene, index) => ({
        ...object,
        [scene.literals.descriptor.index]: {
          ...object[scene.literals.descriptor.index],
          [scene.IRI]: true,
        },
      }),
      {},
    );

    const studioScenes = Object.values(scenes)
      .sort(
        (a, b) =>
          (a.literals.descriptor.index ?? 0) -
          (b.literals.descriptor.index ?? 0),
      )
      .reduce(
        (object, scene, index) => {
          const id = IRIToId(scene.IRI);
          const { descriptor } = scene.literals;

          // Convert text to array if it's a string (from backend)
          const sceneData = descriptor as StudioScene;

          const images = sceneData.images || [];
          const videoInstances = sceneData.videoInstances || [];
          const text = sceneData.texts;
          const normalizedText = text
            ? Array.isArray(text)
              ? text
              : [text]
            : undefined;

          const shapes = Object.entries(sceneData.shapes || {}).reduce(
            (object, [id, shape]) => ({
              ...object,
              [id]: {
                ...shape,
                cell: shape.cell || { w: 1, h: 1, x: 0, y: 0 },
              },
            }),
            {} as Record<string, any>,
          );

          object[id] = {
            id,
            ...(descriptor as StudioScene),
            texts: normalizedText,
            images,
            videoInstances,
            positionIndex: index,
            shapes,
            index: descriptor?.index ?? 0,
            // shapes: {},
          };
          return object;
        },
        {} as Record<string, StudioScene>,
      );

    const scenesByIndex = Object.values(studioScenes).sort(
      (a, b) => a.index - b.index,
    );

    const id = IRIToId(projectResource.IRI);
    const { label, descriptor } = projectResource.literals as {
      label: string;
      descriptor: StudioProject;
    };
    const { mode, fontFamily, colorPalette } = descriptor || {};
    const project = { id, name: label, mode, fontFamily, colorPalette };
    return {
      ...state,
      project,
      scenes: studioScenes,
      currentSceneId: scenesByIndex[0]?.id,
      videos,
      images,
      currentProjectID: id,
      projects: {
        ...state.projects,
        [id]: project,
      },
    };
  }),
  on(deleteProject, (state, { projectID }) => {
    state.deleteProjectState = {
      ...state.deleteProjectState,
      [projectID]: {
        loading: true,
      },
    };
    return state;
  }),
  on(deleteProjectSuccess, (state, { projectID }) => {
    delete state.projects[projectID];
    return state;
  }),
  on(deleteProjectFailure, (state, { projectID, error, code }) => {
    state.deleteProjectState = {
      ...state.deleteProjectState,
      [projectID]: {
        loading: false,
        error,
      },
    };
    return state;
  }),
  on(setProjectLabel, (state, { label }) => {
    state.projects[state.currentProjectID].name = label;
    return state;
  }),
  on(setPanelStable, (state, { value }) => {
    state.panelStable = value;
    return state;
  }),
  on(setSceneDurations, (state, { durations }) => {
    state.sceneDurations = durations;
    return state;
  }),
  on(setCurrentStudioSceneId, (state, { value }) => {
    state.currentSceneId = value;
    return state;
  }),
  on(setCurrentStudioSceneIndex, (state, { value }) => {
    if (value == undefined) {
      const scenesByPositionIndex = Object.values(state.scenes).sort(
        (a, b) => a.positionIndex - b.positionIndex,
      );
      state.currentSceneId =
        scenesByPositionIndex[scenesByPositionIndex.length - 1]?.id;
      return state;
    }
    const currentId = Object.values(state.scenes).find(
      s => s.positionIndex == value,
    )?.id;
    state.currentSceneId = currentId;
    return state;
  }),

  on(resetStudio, () => ({
    ...initialStudioState,
  })),

  on(setStudioError, (state, { value }) => ({
    ...state,
    error: value,
  })),

  on(setStudioMode, (state, { mode }) => ({
    ...state,
    mode,
  })),

  on(setStudioData, (state, { data }) => ({
    ...state,
    data,
  })),

  on(deleteStudioImage, (state, { imageId }) => {
    if (state.images && state.images[imageId]) {
      delete state.images[imageId];
    }
    // Also add to crud deletes
    state.crudEvents.deletes[imageId] = 'image';
    // Remove from posts/patches if exists
    delete state.crudEvents.posts[imageId];
    delete state.crudEvents.patches[imageId];
    return state;
  }),

  on(addStudioImage, (state, { image }) => {
    if (!state.images) {
      state.images = {};
    }
    state.images[image.id] = image;
    // Mark as new resource
    state.crudEvents.posts[image.id] = 'image';
    return state;
  }),

  on(deleteStudioVideo, (state, { videoId }) => {
    if (state.videos && state.videos[videoId]) {
      delete state.videos[videoId];
    }
    // Also add to crud deletes
    state.crudEvents.deletes[videoId] = 'video';
    // Remove from posts/patches if exists
    delete state.crudEvents.posts[videoId];
    delete state.crudEvents.patches[videoId];
    return state;
  }),

  on(addStudioVideo, (state, { video }) => {
    if (!state.videos) {
      state.videos = {};
    }
    state.videos[video.id] = video;
    // Mark as new resource
    state.crudEvents.posts[video.id] = 'video';
    return state;
  }),

  // CRUD event reducers
  on(setStudioResourcePost, (state, { id, resourceType }) => {
    state.crudEvents.posts[id] = resourceType;
    return state;
  }),

  on(setStudioResourcePatch, (state, { id, resourceType }) => {
    // Only patch if not already a post (new resource)
    if (!state.crudEvents.posts[id]) {
      state.crudEvents.patches[id] = resourceType;
    }
    return state;
  }),
  on(setStudioResourceDelete, (state, { id, resourceType }) => {
    // If it was a post (new resource), just remove from posts
    if (state.crudEvents.posts[id]) {
      delete state.crudEvents.posts[id];
    } else {
      // Otherwise mark for deletion
      state.crudEvents.deletes[id] = resourceType;
    }
    // Remove from patches if exists
    delete state.crudEvents.patches[id];
    return state;
  }),

  on(resetStudioCrudEvents, state => ({
    ...state,
    crudEvents: {
      posts: {},
      patches: {},
      deletes: {},
    },
  })),

  on(setSceneDescriptorValueByKey, (state, { sceneId, key, value }) => {
    console.log('setSceneDescriptorValueByKey', { sceneId, key, value });
    if (state.scenes[sceneId]) {
      console.log('setSceneDescriptorValueByKey -- in', {
        sceneId,
        key,
        value,
      });
      // @ts-expect-error - dynamic key assignment
      state.scenes[sceneId][key] = value;
    }
    return state;
  }),
  on(saveScene, (state, { sceneId, value }) => {
    const { currentSceneId } = state;
    state.scenes[sceneId || currentSceneId] = value;
    return state;
  }),
  on(setChangeTookPlace, state => {
    state.changeTookPlace = state.changeTookPlace + 1;
    return state;
  }),

  on(addStudioSceneBase, (state, { atIndex, scene }) => {
    const scenes = Object.values(state.scenes).sort(
      (a, b) => a.positionIndex - b.positionIndex,
    );

    // Shift indices of scenes at or after atIndex
    scenes
      .filter(scene => scene.positionIndex >= atIndex)
      .forEach(scene => {
        state.scenes[scene.id].positionIndex++;
      });

    state.scenes[scene.id] = scene;
    state.currentSceneId = scene.id;
    return state;
  }),
  on(removeStudioScene, (state, { sceneId }) => {
    const sceneToRemove = state.scenes[sceneId];
    if (!sceneToRemove) {
      return state;
    }
    const positionIndex = sceneToRemove.positionIndex;
    console.log('removeStudioScene', { sceneId, positionIndex });
    delete state.scenes[sceneId];

    const scenesByPositionIndex = Object.values(state.scenes).sort(
      (a, b) => a.positionIndex - b.positionIndex,
    );
    const scenes = scenesByPositionIndex.filter(
      scene => scene.positionIndex > positionIndex,
    );

    // let newScen = false;

    if (scenes.length) {
      state.currentSceneId = scenes[0].id;
      scenes.forEach(scene => {
        state.scenes[scene.id].positionIndex--;
      });
    } else {
      state.currentSceneId =
        scenesByPositionIndex[scenesByPositionIndex.length - 1]?.id;
    }
    return state;
  }),
  on(saveShape, (state, { shapeId, shape }) => {
    const currentScene = state.scenes[state.currentSceneId];
    if (!currentScene) {
      return state;
    }
    state.scenes[state.currentSceneId].shapes ||= {};
    state.scenes[state.currentSceneId].shapes[shapeId] = shape;
    return state;
  }),
  on(removeText, (state, { index }) => {
    const currentScene = state.scenes[state.currentSceneId];
    if (!currentScene) {
      return state;
    }

    // Remove the text from the text array
    if (currentScene.texts && currentScene.texts.length > index) {
      currentScene.texts.splice(index, 1);
    }

    // Remove the shape with key `text:${index}`
    const shapeKey = `text:${index}`;
    if (currentScene.shapes && currentScene.shapes[shapeKey]) {
      delete currentScene.shapes[shapeKey];
    }

    // Reindex remaining text shapes
    if (currentScene.shapes) {
      const textShapes = Object.entries(currentScene.shapes)
        .filter(([key]) => key.startsWith('text:'))
        .sort(([keyA], [keyB]) => {
          const indexA = parseInt(keyA.split(':')[1]);
          const indexB = parseInt(keyB.split(':')[1]);
          return indexA - indexB;
        });

      // Clear all text shapes
      Object.keys(currentScene.shapes)
        .filter(key => key.startsWith('text:'))
        .forEach(key => delete currentScene.shapes[key]);

      // Re-add with correct indices
      textShapes.forEach(([, shape], newIndex) => {
        if (newIndex < index) {
          // Keep original index for shapes before the removed one
          currentScene.shapes[`text:${newIndex}`] = shape;
        } else {
          // Shift down indices for shapes after the removed one
          currentScene.shapes[`text:${newIndex}`] = shape;
        }
      });
    }

    return state;
  }),
  on(setCurrentStudioProjectID, (state, { projectID }) => {
    state.currentProjectID = projectID;
    return state;
  }),
  on(selectCell, (state, { cellId }) => {
    state.selectedCells = {
      [cellId]: true,
    };
    return state;
  }),
  on(selectShape, (state, { id }) => {
    state.selectedShapes = {
      [id]: true,
    };
    return state;
  }),
  on(deselectAllCells, state => {
    state.selectedCells = {};
    return state;
  }),
  on(textStyleChange, (state, { style }) => {
    const currentScene = state.scenes[state.currentSceneId];
    if (!currentScene) {
      return state;
    }

    // Get selected cell IDs
    const selectedCellIds = Object.keys(state.selectedCells);

    // If no cells are selected, update all text cells in the current scene
    const cellIdsToUpdate =
      selectedCellIds.length > 0
        ? selectedCellIds
        : Object.keys(currentScene.cells).filter(
            cellId => currentScene.cells[cellId].text,
          );

    // Update each cell's style
    cellIdsToUpdate.forEach(cellId => {
      const cell = state.scenes[state.currentSceneId].cells[cellId];
      if (cell && cell.text) {
        const textCell = cell as TextCell;
        // Initialize style object if it doesn't exist
        if (!textCell.style) {
          textCell.style = {};
        }

        // Update style properties
        if (style.fontFamily !== undefined) {
          textCell.style.fontFamily = style.fontFamily;
        }
        if (style.fontSize !== undefined) {
          textCell.style.fontSize = style.fontSize;
        }
        if (style.color !== undefined) {
          textCell.style.color = style.color;
        }
      }
    });

    return state;
  }),
  on(selectMedia, (state, { id, videoId, videoInstanceId }) => {
    // Find the media in videos or images
    const video = state.videos?.[videoId];
    const instance = video?.instances?.[videoInstanceId];
    state.selectedMedia = instance;
    return state;
  }),
  on(deselectMedia, state => {
    state.selectedMedia = undefined;
    return state;
  }),
  on(deselectAllShapes, state => {
    state.selectedShapes = {};
    return state;
  }),
  on(addVideoInstance, (state, { videoInstance }) => {
    const { id, mediaId: videoId } = videoInstance;
    state.videos[videoId].instances ||= {};
    state.videos[videoId].instances[id] = {
      type: 'video',
      id: id,
      mediaId: videoId,
    };
    // -- // -- // -- // -- // -- //
    console.log('addVideoInstance > state');
    return state;
  }),
  on(updateVideoInstance, (state, { videoId, videoInstanceId, updates }) => {
    const video = state.videos?.[videoId];
    if (video?.instances?.[videoInstanceId]) {
      // Update the video instance with the provided updates
      Object.assign(video.instances[videoInstanceId], updates);
    }
    return state;
  }),
  on(setShapeDescriptorAttribute, (state, { key, value }) => {
    const { selectedShapes } = state;
    const [shapeID] = Object.keys(selectedShapes);
    if (!shapeID) {
      return state;
    }
    console.log('setShapeDescriptorAttribute', { key, value });
    console.log('setShapeDescriptorAttribute >', {
      currentSceneId: state.currentSceneId,
      shapeId: shapeID,
    });
    set(state, ['scenes', state.currentSceneId, 'shapes', shapeID, key], value);
    return state;
  }),
);
